import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import CargomaticModal from 'components/v2/modal';
import { titleize } from 'underscore.string';
import CargomaticInput from '../../../v2/input';

export default function EditLaneRateModal({
  showModal,
  itemMetaData,
  onSave,
  onClose,
}) {
  const { t } = useTranslation();
  const [saveDisabled, _setSaveDisabled] = useState(false);
  const [rate, setRate] = useState(0);
  const [newRate, setNewRate] = useState(0);
  const [adjustmentNote, setAdjustmentNote] = useState('');
  const [errors, setErrors] = useState({
    adjustmentAmount: '',
    adjustmentNote: '',
  });

  useEffect(() => {
    setRate(itemMetaData.carrier_base_cost || 0);
  }, [itemMetaData]);

  const handleCarrierRateChange = (val) => {
    setNewRate(val);
    setErrors((prev) => ({
      ...prev,
      adjustmentAmount: '',
    }));
  };

  const handleAdjustmentNoteChange = (val) => {
    setAdjustmentNote(val);
    setErrors((prev) => ({
      ...prev,
      adjustmentNote: '',
    }));
  };

  const handleSave = () => {
    if (!newRate) {
      return setErrors((prev) => ({
        ...prev,
        adjustmentAmount: t('ADJUSTMENT_AMOUNT_FIELD_ERROR'),
      }));
    }

    if (!adjustmentNote) {
      return setErrors((prev) => ({
        ...prev,
        adjustmentNote: t('ADJUSTMENT_NOTE_FIELD_ERROR'),
      }));
    }

    const bulkAdjustmentMetaData = {
      carrier: itemMetaData.carrier._id,
      shipper: itemMetaData.shipper._id,
      origin_address: itemMetaData.originAddress,
      destination_address: itemMetaData.destinationAddress,
      updated_carrier_base_cost: parseFloat(newRate || 0).toFixed(2),
      carrier_base_cost: itemMetaData.carrier_base_cost,
      shipper_base_cost: itemMetaData.shipper_base_cost,
    };

    onSave(bulkAdjustmentMetaData);
    onClose();
  };

  const getBody = () => {
    const num = itemMetaData.count;
    return (
      <>
        <div
          data-testid="div-f4d054e5"
          className="w-full flex justify-start mt-2"
        >
          <div data-testid="div-30b0d89e" className="mb-4 mr-12">
            <CargomaticInput
              dataTestId="CargomaticInput-e623d14b"
              className="mb-4"
              label={t('CURRENT_TOTAL_CARRIER_RATE_LABEL')}
              info={t('CURRENT_TOTAL_CARRIER_RATE_INFO')}
              maxLength={5}
              type="number"
              value={rate}
              disabled
            />
          </div>
          <div data-testid="div-9b8336a8" className="mb-4">
            <CargomaticInput
              dataTestId="CargomaticInput-3f9667d3"
              className="mb-4"
              error={{ message: errors.adjustmentAmount }}
              label={t('NEW_TOTAL_CARRIER_RATE_LABEL')}
              info={t('INTENDED_VALUE_POST_ADJUSTMENT')}
              onChange={handleCarrierRateChange}
              maxLength={5}
              type="number"
              value={newRate}
            />
          </div>
        </div>
        <CargomaticInput
          dataTestId="CargomaticInput-a37abc0c"
          className="mb-4"
          error={{ message: errors.adjustmentNote }}
          label={t('INVOICE_NOTE')}
          info={t('INVOICE_NOTE_INFO')}
          onChange={handleAdjustmentNoteChange}
          maxLength={50}
          value={adjustmentNote}
        />
        <div
          data-testid="div-17204e93"
          className="text-xs font-bold text-gray-500"
        >
          <Trans i18nKey="CARRIER_RATE_EDIT_MESSAGE">
            You are about to edit the carrier rate for
            <span data-testid="span-f183be68" className="text-teal-600">
              {{ num }}
            </span>
            shipment(s).
          </Trans>
        </div>
      </>
    );
  };

  const getFooter = () => {
    return (
      <div data-testid="div-5fa14a6f" className="flex">
        <div data-testid="div-c3a2224e" className="w-3/4" />
        <div data-testid="div-4cfdccc0" className="w-1/4 mt-3">
          <div
            data-testid="div-58b14718"
            className="flex justify-end button-group"
          >
            <button
              data-testid="button-46d311bd"
              className="block button secondary-action"
              onClick={onClose}
            >
              {t('CANCEL')}
            </button>
            <button
              data-testid="button-14dec67b"
              className="block mr-2 button button-green unified-masterList-assignCarrier"
              disabled={saveDisabled}
              onClick={handleSave}
            >
              {titleize(t('SAVE'))}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <CargomaticModal
      dataTestId="CargomaticModal-fa43f012"
      title={t('EDIT_LANE_RATE_MODAL_TITLE')}
      style={{ top: '30%' }}
      show={showModal}
      onHide={onClose}
      body={getBody()}
      footer={getFooter()}
    />
  );
}

EditLaneRateModal.propTypes = {
  showModal: PropTypes.bool,
  itemMetaData: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
